import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Col, Grid, Row, Select, Skeleton, Space, Statistic, Tag, Typography } from "antd";
import { BarChartOutlined, CalendarOutlined, HomeOutlined, PieChartOutlined, TeamOutlined } from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const { Option } = Select;
const { useBreakpoint } = Grid;
const { Title: TTitle, Text } = Typography;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
};

const AgentDashboard = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState("bar");
  const [stats, setStats] = useState({
    total_hotels: 0,
    total_bookings: 0,
    active_guests: 0,
    monthly_revenue: 0,
    pending_bookings: 0,
    confirmed_bookings: 0,
    cancelled_bookings: 0,
  });

  const agentProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const agentId = agentProfile?.agent_id;

  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    request({
      url: apiPath.agentDashboardCounts(agentId),
      method: "GET",
      onSuccess: (res) => {
        const data = res?.data || {};
        setStats((prev) => ({
          ...prev,
          total_hotels: Number(data.total_hotels || 0),
          total_bookings: Number(data.total_bookings || 0),
          active_guests: Number(data.active_guests || 0),
          monthly_revenue: Number(data.monthly_revenue || 0),
        }));
      },
      onError: () => {
        setStats((prev) => ({
          ...prev,
          total_hotels: 0,
          total_bookings: 0,
          active_guests: 0,
          monthly_revenue: 0,
        }));
      },
      finally: () => setLoading(false),
    });

    request({
      url: apiPath.agentBookingsByAgent(agentId),
      method: "GET",
      onSuccess: (res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const pending = list.filter((b) => b.status === "pending").length;
        const confirmed = list.filter((b) => b.status === "confirmed").length;
        const cancelled = list.filter((b) => b.status === "cancelled").length;
        setStats((prev) => ({
          ...prev,
          pending_bookings: pending,
          confirmed_bookings: confirmed,
          cancelled_bookings: cancelled,
        }));
      },
      onError: () => {
        setStats((prev) => ({
          ...prev,
          pending_bookings: 0,
          confirmed_bookings: 0,
          cancelled_bookings: 0,
        }));
      },
    });
  }, [agentId, request]);

  const metricCards = [
    { key: "total_hotels", title: "Associated Hotels", icon: <HomeOutlined />, color: "#1890ff", bg: "#e6f7ff", path: "/agent-hotels" },
    { key: "total_bookings", title: "Confirmed Bookings", icon: <CalendarOutlined />, color: "#52c41a", bg: "#f6ffed", path: "/agent-picked-bookings" },
    { key: "active_guests", title: "Active Guests", icon: <TeamOutlined />, color: "#fa8c16", bg: "#fff7e6", path: "/agent-picked-bookings" },
    { key: "monthly_revenue", title: "Monthly Revenue", icon: <BarChartOutlined />, color: "#722ed1", bg: "#f9f0ff", isCurrency: true, path: "/agent-picked-bookings" },
  ];

  const chartLabels = ["Pending", "Confirmed", "Cancelled"];
  const chartValues = [stats.pending_bookings, stats.confirmed_bookings, stats.cancelled_bookings];
  const chartColors = ["#faad14", "#52c41a", "#ff4d4f"];

  const barData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: "Bookings",
          data: chartValues,
          backgroundColor: chartColors,
          borderRadius: 10,
          borderSkipped: false,
        },
      ],
    }),
    [stats.pending_bookings, stats.confirmed_bookings, stats.cancelled_bookings]
  );

  const pieData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          data: chartValues,
          backgroundColor: chartColors,
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    }),
    [stats.pending_bookings, stats.confirmed_bookings, stats.cancelled_bookings]
  );

  return (
    <div className="agent-dashboard-page">
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)" }}>
        <TTitle level={screens.xs ? 4 : 3} style={{ margin: 0, fontWeight: 700 }}>
          Agent Dashboard
        </TTitle>
        <Text type="secondary">Track your hotels, bookings, guests and monthly revenue.</Text>
      </Card>

      <Row gutter={[12, 12]}>
        {metricCards.map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.key}>
            <Card bordered={false} hoverable onClick={() => navigate(card.path)} style={{ borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)", background: `linear-gradient(135deg, ${card.bg} 0%, #fff 100%)`, height: "100%", cursor: "pointer" }}>
              <Skeleton active loading={loading} paragraph={{ rows: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: card.bg, display: "flex", alignItems: "center", justifyContent: "center", color: card.color, fontSize: 18 }}>
                    {card.icon}
                  </div>
                  <Text type="secondary" style={{ fontWeight: 600 }}>{card.title}</Text>
                </div>
                <Statistic
                  value={stats[card.key] ?? 0}
                  precision={card.isCurrency ? 0 : undefined}
                  prefix={card.isCurrency ? "$." : undefined}
                  valueStyle={{ color: card.color, fontSize: screens.xs ? 22 : 28, fontWeight: 800 }}
                />
              </Skeleton>
            </Card>
          </Col>
        ))}
      </Row>

      <Card bordered={false} style={{ marginTop: 14, borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
          <Space direction="vertical" size={0}>
            <TTitle level={screens.xs ? 5 : 4} style={{ margin: 0 }}>Bookings Overview</TTitle>
            <Text type="secondary" style={{ fontSize: 12 }}>Status-wise distribution for your bookings</Text>
          </Space>
          <Select value={chartType} onChange={setChartType} style={{ width: screens.xs ? "100%" : 160 }}>
            <Option value="bar"><BarChartOutlined style={{ marginRight: 6 }} />Bar Chart</Option>
            <Option value="pie"><PieChartOutlined style={{ marginRight: 6 }} />Pie Chart</Option>
          </Select>
        </div>

        <Skeleton active loading={loading} paragraph={{ rows: 6 }}>
          <div style={{ height: screens.xs ? 300 : 420, borderRadius: 12, padding: screens.xs ? 8 : 14, background: "#fff" }}>
            {chartType === "bar" ? (
              <Bar
                data={barData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            ) : (
              <Pie
                data={pieData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: screens.xs ? "bottom" : "right" } },
                }}
              />
            )}
          </div>
        </Skeleton>
      </Card>

      <Row gutter={[10, 10]} style={{ marginTop: 14 }}>
        <Col>
          <Tag color="orange">Pending: {stats.pending_bookings}</Tag>
        </Col>
        <Col>
          <Tag color="green">Confirmed: {stats.confirmed_bookings}</Tag>
        </Col>
        <Col>
          <Tag color="red">Cancelled: {stats.cancelled_bookings}</Tag>
        </Col>
      </Row>

      <style>{`
        .agent-dashboard-page .ant-btn-primary:hover,
        .agent-dashboard-page .ant-btn-primary:focus {
          background: ${theme.primaryDark} !important;
          border-color: ${theme.primaryDark} !important;
        }
      `}</style>
    </div>
  );
};

export default AgentDashboard;
